import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function roleLabel(role) {
  const r = Number(role || 0);
  if (r >= 2) return 'Lider';
  if (r >= 1) return 'Gestor';
  return 'Miembro';
}

export default function ClanHub() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [incomingLeave, setIncomingLeave] = useState([]);
  const [aiReviewingRequestId, setAiReviewingRequestId] = useState(null);
  const [subclans, setSubclans] = useState([]);
  const [newSubclanName, setNewSubclanName] = useState('');
  const [bonusesByClan, setBonusesByClan] = useState({});
  const [activeNotice, setActiveNotice] = useState(null);

  const isNeutral = state?.state === 'NEUTRAL' || state?.state === 'PENDING_CLAN_REQUEST';
  const hasPendingJoinRequest = state?.state === 'PENDING_CLAN_REQUEST';
  const myClanId = Number(state?.membership?.id_clan || state?.bot?.clan || 0);
  const isManager = Number(state?.membership?.role || 0) >= 1;

  const myClan = useMemo(() => catalog.find((c) => Number(c.id) === myClanId), [catalog, myClanId]);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [meRes, catalogRes, mineRes, myLeaveRes, bonusesRes] = await Promise.all([
        api.get('/clans/me'),
        api.get('/clans/catalog'),
        api.get('/clans/requests/mine'),
        api.get('/clans/leave-requests/mine').catch(() => ({ data: { requests: [] } })),
        api.get('/clans/bonuses/config').catch(() => ({ data: { bonuses: [] } }))
      ]);
      setState(meRes.data);
      const notices = Array.isArray(meRes.data?.notices) ? meRes.data.notices : [];
      const relocationNotice = notices.find((n) => String(n.notice_type || '') === 'CLAN_RELOCATION');
      setActiveNotice(relocationNotice || null);
      setCatalog(catalogRes.data?.clans || []);
      setMyRequests(mineRes.data?.requests || []);
      setMyLeaveRequests(myLeaveRes.data?.requests || []);
      const bonusMap = {};
      for (const row of bonusesRes.data?.bonuses || []) {
        if (row.subclan_id === null || row.subclan_id === undefined) {
          bonusMap[Number(row.clan_id)] = {
            xp: Number(row.xp_bonus_percent || 0),
            levelPoints: Number(row.level_points_bonus_percent || 0),
            money: Number(row.money_bonus_percent || 0),
            active: Number(row.is_active || 0) === 1
          };
        }
      }
      setBonusesByClan(bonusMap);

      const activeClanId = Number(meRes.data?.membership?.id_clan || meRes.data?.bot?.clan || 0);
      if (activeClanId > 0) {
        const [subRes, incomingRes, incomingLeaveRes] = await Promise.all([
          api.get('/subclans', { params: { clanId: activeClanId } }).catch(() => ({ data: { subclans: [] } })),
          api.get('/clans/requests/incoming').catch(() => ({ data: { requests: [] } })),
          api.get('/clans/leave-requests/incoming').catch(() => ({ data: { requests: [] } }))
        ]);
        setSubclans(subRes.data?.subclans || []);
        setIncoming(incomingRes.data?.requests || []);
        setIncomingLeave(incomingLeaveRes.data?.requests || []);
      } else {
        setSubclans([]);
        setIncoming([]);
        setIncomingLeave([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el centro de clan');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function requestJoin(clanId) {
    setMessage('');
    setError('');
    try {
      await api.post(`/clans/${clanId}/join-request`);
      setMessage('Solicitud enviada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo enviar solicitud');
    }
  }

  async function cancelRequest(requestId) {
    setMessage('');
    setError('');
    try {
      await api.post(`/clans/requests/${requestId}/cancel`);
      setMessage('Solicitud cancelada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cancelar solicitud');
    }
  }

  async function resolveIncoming(requestId, action) {
    setMessage('');
    setError('');
    try {
      await api.post(`/clans/requests/${requestId}/${action}`);
      setMessage(action === 'approve' ? 'Solicitud aprobada.' : 'Solicitud rechazada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo resolver solicitud');
    }
  }

  async function runAiReview(requestId) {
    setMessage('');
    setError('');
    setAiReviewingRequestId(requestId);
    try {
      setMessage('Ejecutando revisión IA...');
      const res = await api.post(`/clans/requests/${requestId}/ai-review`);
      const review = res?.data?.review;
      setMessage(
        `IA: ${review?.recommendation || 'REVIEW'} (score ${review?.score ?? 50}) - ${review?.reason || 'Sin detalle'}`
      );
      await loadAll();
    } catch (err) {
      const base = err.response?.data?.error || 'No se pudo ejecutar revisión IA';
      const detail = err.response?.data?.detail ? ` (${err.response.data.detail})` : '';
      setError(`${base}${detail}`);
    } finally {
      setAiReviewingRequestId(null);
    }
  }

  async function leaveClan() {
    setMessage('');
    setError('');
    try {
      await api.post('/clans/leave-request');
      setMessage('Solicitud de salida enviada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo solicitar salida de clan');
    }
  }

  async function cancelLeaveRequest(requestId) {
    setMessage('');
    setError('');
    try {
      await api.post(`/clans/leave-requests/${requestId}/cancel`);
      setMessage('Solicitud de salida cancelada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cancelar solicitud de salida');
    }
  }

  async function resolveIncomingLeave(requestId, action) {
    setMessage('');
    setError('');
    try {
      await api.post(`/clans/leave-requests/${requestId}/${action}`);
      setMessage(action === 'approve' ? 'Salida aprobada.' : 'Salida rechazada.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo resolver solicitud de salida');
    }
  }

  async function joinSubclan(subclanId) {
    setMessage('');
    setError('');
    try {
      await api.post(`/subclans/${subclanId}/join`);
      setMessage('Te uniste al subclan.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo unir al subclan');
    }
  }

  async function createSubclan() {
    const name = newSubclanName.trim();
    if (!name) return;
    setMessage('');
    setError('');
    try {
      await api.post('/subclans', { name, clanId: myClanId });
      setNewSubclanName('');
      setMessage('Subclan creado.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo crear subclan');
    }
  }

  async function dissolveSubclan(subclanId) {
    setMessage('');
    setError('');
    try {
      await api.delete(`/subclans/${subclanId}`);
      setMessage('Subclan disuelto.');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo disolver subclan');
    }
  }

  async function closeNotice() {
    if (!activeNotice?.id) {
      setActiveNotice(null);
      return;
    }
    try {
      await api.post(`/clans/notices/${activeNotice.id}/read`);
    } catch (_err) {
      // Si falla, cerramos modal igualmente para no bloquear UI.
    } finally {
      setActiveNotice(null);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando centro de clan...</div>;
  }

  return (
    <div className="min-h-screen bg-[#04060d] text-slate-100">
      {activeNotice && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl border border-cyan-400/40 bg-[#08101f] p-5 shadow-[0_0_40px_rgba(0,255,255,0.18)]">
            <h3 className="text-cyan-300 font-bold text-xl">{activeNotice.title || 'Aviso de traslado'}</h3>
            <p className="text-slate-200 mt-3">{activeNotice.body || 'Tu BOT ha sido trasladado a una ciudad acorde a tu clan.'}</p>
            <p className="text-slate-400 text-sm mt-2">Confirma para continuar.</p>
            <div className="mt-5 flex justify-end">
              <button onClick={closeNotice} className="rounded-lg border border-cyan-400/70 px-4 py-2 text-cyan-200 hover:bg-cyan-500/10">
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-5">
        <div className="rounded-2xl border border-cyan-500/30 bg-[#091225] p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-cyan-300">Centro de Clan/Subclan</h1>
              <p className="text-sm text-slate-300/80 mt-1">
                Estado: {state?.state || '-'} {myClan ? `| Clan actual: ${myClan.name}` : ''}
              </p>
            </div>
            <button onClick={() => navigate('/dashboard')} className="rounded-lg border border-cyan-400/60 px-4 py-2 text-cyan-200">
              Volver al panel
            </button>
          </div>
          {!!message && <p className="mt-3 text-emerald-300 text-sm">{message}</p>}
          {!!error && <p className="mt-3 text-rose-300 text-sm">{error}</p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Bonos activos</h2>
            <div className="text-sm text-slate-300 space-y-2">
              <div>EXP clan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.clan?.xp || 0}%</span></div>
              <div>EXP subclan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.subclan?.xp || 0}%</span></div>
              <div>Puntos clan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.clan?.levelPoints || 0}%</span></div>
              <div>Puntos subclan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.subclan?.levelPoints || 0}%</span></div>
              <div>Oro clan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.clan?.money || 0}%</span></div>
              <div>Oro subclan: <span className="text-cyan-200 font-bold">+{state?.bonuses?.subclan?.money || 0}%</span></div>
            </div>
            {myClan && bonusesByClan[myClan.id] && (
              <p className="text-xs text-slate-400 mt-3">
                Perfil clan ({myClan.name}): +{bonusesByClan[myClan.id].xp}% EXP, +{bonusesByClan[myClan.id].levelPoints}% puntos, +{bonusesByClan[myClan.id].money}% oro
              </p>
            )}
          </section>

          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Tu estado</h2>
            <div className="text-sm text-slate-300 space-y-2">
              <div>Clan ID: <span className="text-cyan-200 font-bold">{myClanId || '-'}</span></div>
              <div>Rango: <span className="text-cyan-200 font-bold">{roleLabel(state?.membership?.role)}</span></div>
              <div>Subclan: <span className="text-cyan-200 font-bold">{state?.membership?.subclan_id || state?.bot?.subclan_id || 'Ninguno'}</span></div>
            </div>
            {!isNeutral && (
              <button onClick={leaveClan} className="mt-4 rounded-lg border border-rose-400/60 px-4 py-2 text-rose-200">
                Solicitar salida de clan
              </button>
            )}
          </section>
        </div>

        {isNeutral && !hasPendingJoinRequest && (
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Clanes disponibles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {catalog.filter((c) => Number(c.id) > 1).map((clan) => (
                <div key={clan.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18]">
                  <div className="font-bold text-cyan-200">{clan.name}</div>
                  <div className="text-xs text-slate-400">Miembros: {clan.member_count}</div>
                  <div className="text-xs text-slate-300 mt-2">
                    Ventajas: <span className="text-cyan-200">+{bonusesByClan[clan.id]?.xp ?? 0}% EXP</span> |{' '}
                    <span className="text-cyan-200">+{bonusesByClan[clan.id]?.levelPoints ?? 0}% puntos</span> |{' '}
                    <span className="text-cyan-200">+{bonusesByClan[clan.id]?.money ?? 0}% oro</span>
                  </div>
                  <button onClick={() => requestJoin(clan.id)} className="mt-2 rounded-lg border border-cyan-400/60 px-3 py-1 text-cyan-200">
                    Solicitar ingreso
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="text-cyan-300 font-bold mb-3">Tus solicitudes</h2>
          <div className="space-y-2">
            {myRequests.length === 0 && <p className="text-sm text-slate-400">Sin solicitudes.</p>}
            {myRequests.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18] flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-cyan-200 font-semibold">{r.target_clan_name || `Clan #${r.target_clan_id}`}</div>
                  <div className="text-slate-400">Estado: {r.status}</div>
                </div>
                {r.status === 'PENDING' && (
                  <button onClick={() => cancelRequest(r.id)} className="rounded-lg border border-rose-400/60 px-3 py-1 text-rose-200">Cancelar</button>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="text-cyan-300 font-bold mb-3">Tus solicitudes de salida</h2>
          <div className="space-y-2">
            {myLeaveRequests.length === 0 && <p className="text-sm text-slate-400">Sin solicitudes de salida.</p>}
            {myLeaveRequests.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18] flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-cyan-200 font-semibold">{r.clan_name || `Clan #${r.clan_id}`}</div>
                  <div className="text-slate-400">Estado: {r.status}</div>
                </div>
                {r.status === 'PENDING' && (
                  <button onClick={() => cancelLeaveRequest(r.id)} className="rounded-lg border border-rose-400/60 px-3 py-1 text-rose-200">Cancelar</button>
                )}
              </div>
            ))}
          </div>
        </section>

        {!isNeutral && (
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Subclanes del clan</h2>
            {isManager && (
              <div className="mb-4 flex gap-2">
                <input
                  value={newSubclanName}
                  onChange={(e) => setNewSubclanName(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-700 bg-[#060c18] px-3 py-2 text-slate-100"
                  placeholder="Nombre nuevo subclan"
                />
                <button onClick={createSubclan} className="rounded-lg border border-cyan-400/60 px-3 py-2 text-cyan-200">Crear</button>
              </div>
            )}
            <div className="space-y-2">
              {subclans.length === 0 && <p className="text-sm text-slate-400">No hay subclanes.</p>}
              {subclans.map((s) => (
                <div key={s.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18] flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-cyan-200 font-semibold">{s.name}</div>
                    <div className="text-slate-400">Miembros: {s.member_count}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => joinSubclan(s.id)} className="rounded-lg border border-cyan-400/60 px-3 py-1 text-cyan-200">Unirme</button>
                    {isManager && (
                      <button onClick={() => dissolveSubclan(s.id)} className="rounded-lg border border-rose-400/60 px-3 py-1 text-rose-200">Disolver</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {isManager && !isNeutral && (
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Solicitudes entrantes</h2>
            <div className="space-y-2">
              {incoming.length === 0 && <p className="text-sm text-slate-400">Sin solicitudes pendientes.</p>}
              {incoming.map((req) => (
                <div key={req.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18] flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-cyan-200 font-semibold">{req.username} ({req.bot_name})</div>
                    <div className="text-slate-400">{req.email}</div>
                    {req.ai_recommendation && (
                      <div className="mt-1 text-xs text-amber-200">
                        IA: {req.ai_recommendation} | score {req.ai_score ?? 50} | riesgo {req.ai_risk || 'MEDIUM'}
                        {req.ai_reason ? ` | ${req.ai_reason}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => runAiReview(req.id)}
                      disabled={aiReviewingRequestId === req.id}
                      className="rounded-lg border border-indigo-400/60 px-3 py-1 text-indigo-200 disabled:opacity-60"
                    >
                      {aiReviewingRequestId === req.id ? 'Procesando IA...' : 'Revisión IA'}
                    </button>
                    <button onClick={() => resolveIncoming(req.id, 'approve')} className="rounded-lg border border-emerald-400/60 px-3 py-1 text-emerald-200">Aprobar</button>
                    <button onClick={() => resolveIncoming(req.id, 'reject')} className="rounded-lg border border-rose-400/60 px-3 py-1 text-rose-200">Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {isManager && !isNeutral && (
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="text-cyan-300 font-bold mb-3">Solicitudes de salida entrantes</h2>
            <div className="space-y-2">
              {incomingLeave.length === 0 && <p className="text-sm text-slate-400">Sin solicitudes pendientes.</p>}
              {incomingLeave.map((req) => (
                <div key={req.id} className="rounded-lg border border-slate-700 p-3 bg-[#060c18] flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-cyan-200 font-semibold">{req.username} ({req.bot_name})</div>
                    <div className="text-slate-400">{req.email}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => resolveIncomingLeave(req.id, 'approve')} className="rounded-lg border border-emerald-400/60 px-3 py-1 text-emerald-200">Aprobar</button>
                    <button onClick={() => resolveIncomingLeave(req.id, 'reject')} className="rounded-lg border border-rose-400/60 px-3 py-1 text-rose-200">Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
